// BuyFlow deep-link builders (WAT-11276).
//
// An authored `pkg.ctaUrl` wins; the derived patterns below are the fallback for a product with no
// authored row:
//   broad (Signature)    -> /shop/dtvi/{slug}-package
//   focused (genre pack)  -> /shop/build/configure?preConfigItems={CODE[,CODE]}&genreSortCategory={FIRST}
//
// preConfigItems codes come from the packages-config `queryparam` field, uppercased — NEVER derived
// from ATT_TV_* ids (MYNEWS != MYNEWS_LOCALS). Returns null when a required code is missing so a
// partial pre-config can never ship; callers hide the CTA instead.

import { Catalog, CatalogPackage } from '../engine/engine.types';

// genreSortCategory's contract value is a GENRE code, so combo SKUs remap to their first
// constituent. Live-checked 2026-08-05: a raw duo SKU also sorted, but by apparent substring match —
// unowned behaviour, so it is not something to rely on. Unknown values fall back to the default rail
// rather than breaking, so an unmapped authored combo degrades gracefully.
const GENRE_SORT_CATEGORY_OVERRIDES = new Map([
  ['MYSPORTSMYNEWSDUO', 'MYSPORTS'],
  ['MYENTERTAINMENTMYNEWSDUO', 'MYENTERTAINMENT']
]);

export const buildBroadCtaUrl = (pkg?: CatalogPackage | null): string | null => {
  if (!pkg) {
    return null;
  }
  // Returned as-is: already scheme- and origin-checked by adaptCatalog, the only PRODUCTION producer
  // of this value. Fixtures and the cms mock set it directly, and nothing here will catch those.
  if (pkg.ctaUrl) {
    return pkg.ctaUrl;
  }
  if (!pkg.slug) {
    return null;
  }
  return `/shop/dtvi/${pkg.slug}-package`;
};

export const buildFocusedCtaUrl = (focusedIds: string[], catalog: Catalog): string | null => {
  if (!focusedIds || focusedIds.length === 0) {
    return null;
  }
  const packs = focusedIds.map(id => catalog.genrePacks.find(g => g.id === id));
  // Single pack only: that is the one shape an authored per-product URL can describe, and the only
  // shape the engine emits (engine.ts:348, :584). Checked BEFORE the shopCode guard because an
  // authored URL carries its own preConfigItems, so it must still work for a product whose
  // packages-config queryparam is missing — the case the derived builder has to refuse.
  const single = packs.length === 1 ? packs[0] : undefined;
  if (single && single.ctaUrl) {
    return single.ctaUrl;
  }
  if (packs.some(p => !p || !p.shopCode)) {
    return null;
  }
  const codes = packs.map(p => (p as CatalogPackage).shopCode);
  const first = codes[0] as string;
  const sortCategory = GENRE_SORT_CATEGORY_OVERRIDES.get(first) ?? first;
  return `/shop/build/configure?preConfigItems=${encodeURIComponent(codes.join(','))}&genreSortCategory=${encodeURIComponent(sortCategory)}`;
};
